import { Box, Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerNotification } from 'types/engagement.types';
import { relativeTime } from './notification-logic';

interface Props {
  items: CustomerNotification[];
  onOpenCta(n: CustomerNotification): void;
  onMarkRead(n: CustomerNotification): void;
  dense?: boolean;
}

/** One button per Mitteilung, leading straight to where something needs doing. */
const NotificationList: React.FC<Props> = ({ items, onOpenCta, onMarkRead, dense = false }) => {
  const { t, i18n } = useTranslation();
  return (
    <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'greys.lighter' }} />}>
      {items.map((n) => {
        const unread = !n.readAt;
        return (
          <Box
            key={n._id}
            data-testid={'notification-item'}
            data-unread={unread ? 'true' : 'false'}
            sx={{ p: dense ? 1.5 : 2, bgcolor: unread ? 'rgba(79, 68, 255, 0.04)' : 'transparent', position: 'relative' }}
          >
            <Stack direction={'row'} gap={1} alignItems={'flex-start'}>
              <Box sx={{ width: 8, height: 8, mt: '6px', borderRadius: '50%', bgcolor: unread ? 'primary.main' : 'transparent', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: unread ? 700 : 500 }}>{n.title}</Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>{n.body}</Typography>
                <Stack direction={'row'} alignItems={'center'} gap={1.5} sx={{ mt: 1 }}>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{relativeTime(n.createdAt, i18n.language)}</Typography>
                  {n.ctaLabel && n.ctaUrl ? (
                    <Button size={'small'} variant={'contained'} sx={{ textTransform: 'none', py: 0.25 }} onClick={() => onOpenCta(n)}>
                      {n.ctaLabel}
                    </Button>
                  ) : null}
                  {unread ? (
                    <Button size={'small'} sx={{ textTransform: 'none', py: 0.25 }} onClick={() => onMarkRead(n)}>
                      {t('engNotifMarkRead')}
                    </Button>
                  ) : null}
                </Stack>
              </Box>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default NotificationList;
